import { Purchases } from '@revenuecat/purchases-capacitor';
import { useGameStore } from '../../store/gameStore';
import { FoundColor, IAPColor } from '@/types/types';
import { useSyncStore } from '../../store/syncStore';

/**
 * Buy a color unlock via RevenueCat
 */
export type PurchaseStatus = 'success' | 'cancelled' | 'error' | 'nothing_to_restore';

export async function buyColor(
  unlockColor: FoundColor,
  productId: IAPColor
): Promise<PurchaseStatus> {
  try {
    const offerings = await Purchases.getOfferings();
    const product = offerings.current?.availablePackages.find(
      (pkg) => pkg.identifier === productId
    );

    if (!product) throw new Error(`Product ${productId} not found in offerings`);

    const { customerInfo } = await Purchases.purchasePackage({ aPackage: product });
    const purchasedIds = customerInfo.allPurchasedProductIdentifiers;

    if (purchasedIds.includes(productId)) {
      useGameStore.getState().addUnlockedColor(unlockColor);
      console.log(`[IAP] Successfully unlocked: ${unlockColor}`);
      return 'success';
    } else {
      throw new Error(`Product ${productId} not found in purchased identifiers`);
    }
  } catch (err: any) {
    if (err.errorMessage === 'Purchase was cancelled.') {
      console.log('[IAP] Purchase cancelled by user');
      return 'cancelled';
    } else {
      console.error('[IAP] Purchase error', err);
      return 'error';
    }
  }
}

export async function buyStatSync(): Promise<PurchaseStatus> {
  try {
    const offerings = await Purchases.getOfferings();
    const product = offerings.current?.availablePackages.find(
      (pkg) => pkg.identifier === 'unlock_stat_sync'
    );

    if (!product) throw new Error(`Productunlock_stat_sync not found in offerings`);

    const { customerInfo } = await Purchases.purchasePackage({ aPackage: product });
    const purchasedIds = customerInfo.allPurchasedProductIdentifiers;

    if (purchasedIds.includes('unlock_stat_sync')) {
      useSyncStore.getState().setStatSyncPurchased();
      console.log(`[IAP] Successfully unlocked: stat sync`);
      return 'success';
    } else {
      throw new Error(`Product ${'unlock_stat_sync'} not found in purchased identifiers`);
    }
  } catch (err: any) {
    if (err.errorMessage === 'Purchase was cancelled.') {
      console.log('[IAP] Purchase cancelled by user');
      return 'cancelled';
    } else {
      console.error('[IAP] Purchase error', err);
      return 'error';
    }
  }
}

/**
 * Restore previously purchased colors via RevenueCat
 */
export async function restorePurchases(): Promise<PurchaseStatus> {
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    const purchased = customerInfo.allPurchasedProductIdentifiers;

    const unlockMap: Record<IAPColor, FoundColor> = {
      unlock_fire_color: 'fire',
      unlock_ice_color: 'ice',
    };

    let restoredSomething = false;

    if (purchased.includes('unlock_stat_sync')) {
      useSyncStore.getState().setStatSyncPurchased();
      console.log(`[IAP] Restored unlocked: stat sync`);
      restoredSomething = true;
    }

    for (const [productId, color] of Object.entries(unlockMap)) {
      if (purchased.includes(productId)) {
        useGameStore.getState().addUnlockedColor(color);
        console.log(`[IAP] Restored unlock: ${color}`);
        restoredSomething = true;
      }
    }

    return restoredSomething ? 'success' : 'nothing_to_restore';
  } catch (err) {
    console.error('[IAP] Restore error', err);
    return 'error';
  }
}
